import { useCallback, useState } from "react";
import { useMutation, useSubscription } from "@apollo/client";
import { useUser } from "@/shared/hooks";
import {
  PollStatusType,
  type PollUpdatedSubscriptionType,
  type PollUpdatedSubscriptionVariablesType,
  type VotePollMutationType,
  type VotePollMutationVariablesType,
} from "@/types/graphql/site.generated";
import { PollUpdatedSubscription, VotePollMutation } from "../api/graphql";
import { mergePoll } from "../lib/selectPollToShow";
import type { SitePoll } from "./types";

export function usePolls({
  contentId,
  initialPolls,
}: {
  contentId: string;
  initialPolls: SitePoll[];
}) {
  const { user, openSignIn } = useUser();
  const [polls, setPolls] = useState<SitePoll[]>(initialPolls);
  const [closedAtById, setClosedAtById] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [votedInSession, setVotedInSession] = useState<Set<string>>(
    () => new Set(),
  );
  // Опрос, голос за который не ушёл: карточка показывает строку ошибки.
  const [voteFailedPollId, setVoteFailedPollId] = useState<string | null>(null);

  // Экран видео не размонтирует плеер при смене контента: сбрасываем
  // синхронно (тот же приём, что в VideoAnnotationsOverlay).
  const [lastContentId, setLastContentId] = useState(contentId);
  if (lastContentId !== contentId) {
    setLastContentId(contentId);
    setPolls(initialPolls);
    setClosedAtById(new Map());
    setVotedInSession(new Set());
    setVoteFailedPollId(null);
  }

  const merge = useCallback((poll: SitePoll) => {
    setPolls((prev) => mergePoll(prev, poll));
    if (poll.status === PollStatusType.ClosedType) {
      setClosedAtById((prev) =>
        prev.has(poll.id) ? prev : new Map(prev).set(poll.id, Date.now()),
      );
    }
  }, []);

  useSubscription<
    PollUpdatedSubscriptionType,
    PollUpdatedSubscriptionVariablesType
  >(PollUpdatedSubscription, {
    variables: { contentId },
    onData: ({ data }) => {
      const poll = data.data?.pollUpdated;
      if (poll) merge(poll);
    },
  });

  const [votePoll, { loading: voting }] = useMutation<
    VotePollMutationType,
    VotePollMutationVariablesType
  >(VotePollMutation);

  const vote = useCallback(
    async (pollId: string, optionIds: string[]) => {
      if (!user) {
        openSignIn();
        return;
      }
      try {
        const result = await votePoll({ variables: { pollId, optionIds } });
        const poll = result.data?.votePoll;
        if (poll) {
          merge(poll);
          setVotedInSession((prev) => new Set(prev).add(pollId));
          setVoteFailedPollId(null);
        }
      } catch (error) {
        // Карточка остаётся с выбором и строкой ошибки, зритель может
        // нажать ещё раз.
        console.error("Failed to vote in poll", error);
        setVoteFailedPollId(pollId);
      }
    },
    [user, openSignIn, votePoll, merge],
  );

  const clearVoteError = useCallback(() => setVoteFailedPollId(null), []);

  return {
    polls,
    closedAtById,
    votedInSession,
    vote,
    voting,
    voteFailedPollId,
    clearVoteError,
  };
}
